// deps
import { useContext, useCallback, useMemo } from "react";

// internal-deps
import {
    GlobalHeaderState,
    CheckoutContext,
    AnalyticsContext,
    CheckoutActionTypes,
    useNavigate,
    CheckoutRoute,
} from "@swiggy-private/checkout-core";
import { SwiggyMoneyActions } from "@swiggy-private/payment-ui";
import { MARKETPLACE_BUSINESS_LINE } from "@swiggy-private/payment-types";

// helpers
import { cacheOriginalBusinessLine } from "@payment-client/helpers/SwiggyInstrumentsHelper";

// events
import { onAddSwiggyMoneyImpressionEvent, onAddSwiggyMoneyClickEvent } from "@payment-client/analytics/events/Payment";
import {
    clickSwiggyMoneyInfoCta,
    closeSwiggyMoneyInfoHalfCard,
    impressionSwiggyMoneyInfoHalfCard,
    impressionSwiggyMoneyInfoCta,
} from "@payment-client/analytics/events/SwiggyMoneyInfoHalfCard";

//constants
import { SCREEN_NAME, CONTEXT } from "@payment-client/analytics/constants";

// contexts
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";

export const useSwiggyMoneyActions = (): SwiggyMoneyActions => {
    const { setCheckoutState } = useContext(CheckoutContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { billTotal } = useContext(PaymentPayContext);
    const navigateTo = useNavigate();

    const onClickAddMoneyCTA = useCallback(() => {
        onAddSwiggyMoneyClickEvent(clickEvent);
        setCheckoutState({ type: CheckoutActionTypes.SHOW_SWIGGYMONEY_PAYMENTS });
        navigateTo(CheckoutRoute.ADD_SWIGGY_MONEY, {
            billTotal,
        });
        cacheOriginalBusinessLine();
        GlobalHeaderState.updateHeaders({
            paymentamount: "",
            marketplacebusinessline: MARKETPLACE_BUSINESS_LINE.SWIGGY_MONEY,
        });
    }, [billTotal, clickEvent, navigateTo, setCheckoutState]);

    const onViewAddMoneyCTA = useCallback((): void => {
        onAddSwiggyMoneyImpressionEvent(impressionEvent);
    }, [impressionEvent]);

    const addMoneyActions = {
        onAddMoneyClick: onClickAddMoneyCTA,
        onAddMoneyView: onViewAddMoneyCTA,
    };

    const onSwiggyMoneyInfoCtaClick = useCallback(() => {
        clickSwiggyMoneyInfoCta(SCREEN_NAME.PAYMENT, clickEvent);
    }, [clickEvent]);

    const onClickSwiggyMoneyInfoHalfCardActionButton = useCallback(() => {
        closeSwiggyMoneyInfoHalfCard(
            SCREEN_NAME.PAYMENT,
            CONTEXT.CLICK_SWIGGY_MONEY_INFO_HALF_CARD_ACTION_BUTTON,
            clickEvent,
        );
    }, [clickEvent]);

    const onClickSwiggyMoneyInfoHalfCardCloseButton = useCallback(() => {
        closeSwiggyMoneyInfoHalfCard(
            SCREEN_NAME.PAYMENT,
            CONTEXT.CLICK_SWIGGY_MONEY_INFO_HALF_CARD_CLOSE_BUTTON,
            clickEvent,
        );
    }, [clickEvent]);

    const onImpressionSwiggyMoneyInfoHalfCard = useCallback(() => {
        impressionSwiggyMoneyInfoHalfCard(SCREEN_NAME.PAYMENT, impressionEvent);
    }, [impressionEvent]);

    const onSwiggyMoneyInfoCtaImpression = useCallback(() => {
        impressionSwiggyMoneyInfoCta(SCREEN_NAME.PAYMENT, impressionEvent);
    }, [impressionEvent]);

    const swiggyMoneyInfoActions: SwiggyMoneyActions["swiggyMoneyInfoActions"] = useMemo(
        () => ({
            onSwiggyMoneyInfoCtaClick,
            onClickSwiggyMoneyInfoHalfCardActionButton,
            onClickSwiggyMoneyInfoHalfCardCloseButton,
            onImpressionSwiggyMoneyInfoHalfCard,
            onSwiggyMoneyInfoCtaImpression,
        }),
        [
            onSwiggyMoneyInfoCtaClick,
            onClickSwiggyMoneyInfoHalfCardActionButton,
            onClickSwiggyMoneyInfoHalfCardCloseButton,
            onSwiggyMoneyInfoCtaImpression,
            onImpressionSwiggyMoneyInfoHalfCard,
        ],
    );

    return { addMoneyActions, swiggyMoneyInfoActions };
};
